import { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  Group,
  Text,
  Button,
  Badge,
  Divider,
  Box,
  Paper,
  Anchor,
  Loader,
  Center,
  ScrollArea,
  Textarea,
  Alert,
} from "@mantine/core";
import {
  IconCheck,
  IconX,
  IconArrowLeft,
  IconFlask,
  IconCube,
  IconAlertTriangle,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import dayjs from "dayjs";
import { useForm } from "@mantine/form";
import {
  SendoutRequestType,
  ExternalInstitutionReason,
  SendOutPriority,
} from "../../services/sendOuts.service";
import {
  useApproveSendOut,
  useRejectSendOut,
  useSendOutWithCaseDetails,
} from "../../hooks/useSendOuts";
import {
  required,
  scrollToFirstError,
  showValidationNotification,
} from "../../utils/formValidation";

type ModalView = "review" | "reject" | "confirm-approve";

interface SendOutApprovalModalProps {
  opened: boolean;
  onClose: () => void;
  sendOutId: string | null;
  onSuccess?: () => void;
}

function formatExternalInstitutionReason(
  reason: ExternalInstitutionReason,
  other?: string | null
): string {
  switch (reason) {
    case ExternalInstitutionReason.CONTINUATION_OF_CARE:
      return "Continuation of Care";
    case ExternalInstitutionReason.LEGAL_REQUEST:
      return "Legal Request";
    case ExternalInstitutionReason.OUTSIDE_REQUEST_ADDITIONAL_TESTING:
      return "Outside Request for Additional Testing";
    case ExternalInstitutionReason.OTHER:
      return other ? `Other: ${other}` : "Other";
    default:
      return reason;
  }
}

function getPriorityColor(priority: SendOutPriority): string {
  switch (priority) {
    case SendOutPriority.STAT:
      return "red";
    case SendOutPriority.URGENT:
      return "orange";
    case SendOutPriority.ROUTINE:
      return "blue";
    default:
      return "gray";
  }
}

function getPriorityLabel(priority: SendOutPriority): string {
  switch (priority) {
    case SendOutPriority.STAT:
      return "STAT";
    case SendOutPriority.URGENT:
      return "Urgent";
    case SendOutPriority.ROUTINE:
      return "Routine";
    default:
      return priority;
  }
}

function formatRequestType(type: SendoutRequestType): string {
  switch (type) {
    case SendoutRequestType.PHYSICIAN_REQUEST:
      return "Ordering Physician Requesting Consultation";
    case SendoutRequestType.EXTERNAL_INSTITUTION:
      return "External Request";
    case SendoutRequestType.GCP_CONSULT:
      return "Pathologist Requires Expert Consultation";
    case SendoutRequestType.MATERIAL_RETURN:
      return "Material Return";
    default:
      return type;
  }
}

export default function SendOutApprovalModal({
  opened,
  onClose,
  sendOutId,
  onSuccess,
}: SendOutApprovalModalProps) {
  const [view, setView] = useState<ModalView>("review");
  const approveMutation = useApproveSendOut();
  const rejectMutation = useRejectSendOut();

  const { data: sendOut, isLoading } = useSendOutWithCaseDetails(
    opened ? sendOutId || undefined : undefined
  );

  const rejectForm = useForm({
    initialValues: { rejectionReason: "" },
    validate: {
      rejectionReason: required("Rejection reason is required"),
    },
  });
  const resetRejectForm = rejectForm.reset;

  // Reset view and form when modal opens/closes
  useEffect(() => {
    if (opened) {
      setView("review");
      resetRejectForm();
    }
  }, [opened, resetRejectForm]);

  const handleApprove = async () => {
    if (!sendOut) return;
    try {
      await approveMutation.mutateAsync(sendOut.id);
      notifications.show({
        title: "Approved",
        message: `Send-out for ${
          sendOut.case?.caseNumber || "case"
        } approved and sent to shipping`,
        color: "green",
      });
      onSuccess?.();
    } catch (error: any) {
      const message =
        error?.response?.data?.message || "Failed to approve send-out";
      showValidationNotification(message);
    }
  };

  const handleReject = async (values: { rejectionReason: string }) => {
    if (!sendOut) return;
    try {
      await rejectMutation.mutateAsync({
        id: sendOut.id,
        reason: values.rejectionReason,
      });
      notifications.show({
        title: "Rejected",
        message: `Send-out for ${
          sendOut.case?.caseNumber || "case"
        } has been rejected`,
        color: "orange",
      });
      rejectForm.reset();
      onSuccess?.();
    } catch (error: any) {
      const message =
        error?.response?.data?.message || "Failed to reject send-out";
      showValidationNotification(message);
    }
  };

  const slideCount = sendOut?.slidesSent?.length || 0;
  const resolvedBlockCount = sendOut?.blocksSent?.length || 0;

  const caseNumber = sendOut?.case?.caseNumber || "";
  const caseId = sendOut?.caseId || "";

  const modalTitlePrefix =
    view === "review"
      ? "Review Send-Out Request"
      : view === "reject"
      ? "Reject Send-Out Request"
      : "Confirm Approval";

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <Text fw={600}>{modalTitlePrefix}</Text>
          {caseNumber && (
            <>
              <Text fw={600}>for</Text>
              <Anchor
                href={`/cases/${caseId}`}
                target="_blank"
                fw={700}
                underline="hover"
                onClick={(e) => e.stopPropagation()}
              >
                {caseNumber}
              </Anchor>
            </>
          )}
        </Group>
      }
      size="xl"
      closeOnClickOutside={false}
    >
      {isLoading || !sendOut ? (
        <Center p="xl">
          <Loader />
        </Center>
      ) : view === "review" ? (
        // ===== REVIEW VIEW =====
        <>
          <ScrollArea.Autosize mah="calc(80vh - 150px)" offsetScrollbars>
            <Stack gap="sm" p={4}>
              {/* Request Info */}
              <Group justify="space-between" wrap="wrap">
                <Box>
                  <Text size="xs" c="dimmed">
                    Request Type
                  </Text>
                  <Badge color="blue" variant="light" mt={4}>
                    {formatRequestType(sendOut.requestType)}
                  </Badge>
                </Box>
                {sendOut.priority && (
                  <Box>
                    <Text size="xs" c="dimmed">
                      Priority
                    </Text>
                    <Badge
                      color={getPriorityColor(sendOut.priority)}
                      variant="filled"
                      size="sm"
                      mt={4}
                    >
                      {getPriorityLabel(sendOut.priority)}
                    </Badge>
                  </Box>
                )}
                <Box>
                  <Text size="xs" c="dimmed">
                    Requested By
                  </Text>
                  <Text fw={500}>{sendOut.requestedBy?.fullName || "N/A"}</Text>
                </Box>
                <Box>
                  <Text size="xs" c="dimmed">
                    Requested
                  </Text>
                  <Text fw={500}>
                    {dayjs(sendOut.createdAt).format("MMM D, YYYY h:mm A")}
                  </Text>
                </Box>
              </Group>

              <Group gap="xl" wrap="wrap">
                <Box>
                  <Text size="xs" c="dimmed">
                    Destination
                  </Text>
                  <Text fw={500}>
                    {sendOut.externalInstitution?.name || "N/A"}
                    {sendOut.externalInstitution?.city &&
                      sendOut.externalInstitution?.state && (
                        <Text span size="sm" c="dimmed">
                          {" "}
                          ({sendOut.externalInstitution.city},{" "}
                          {sendOut.externalInstitution.state})
                        </Text>
                      )}
                  </Text>
                </Box>
                {(sendOut.orderingInstitution?.name ||
                  sendOut.case?.facility?.name) && (
                  <Box style={{ minWidth: 160 }}>
                    <Text size="xs" c="dimmed">
                      Ordering Institution
                    </Text>
                    <Text fw={500} size="sm">
                      {sendOut.orderingInstitution?.name ||
                        sendOut.case?.facility?.name}
                    </Text>
                  </Box>
                )}
                {sendOut.contactPerson && (
                  <Box style={{ minWidth: 140 }}>
                    <Text size="xs" c="dimmed">
                      Contact Person
                    </Text>
                    <Text fw={500} size="sm">
                      {sendOut.contactPerson}
                    </Text>
                  </Box>
                )}
                {sendOut.contactPhoneFax && (
                  <Box style={{ minWidth: 130 }}>
                    <Text size="xs" c="dimmed">
                      Phone / Fax
                    </Text>
                    <Text fw={500} size="sm">
                      {sendOut.contactPhoneFax}
                    </Text>
                  </Box>
                )}
                {sendOut.authorizationReference && (
                  <Box style={{ minWidth: 150 }}>
                    <Text size="xs" c="dimmed">
                      Auth. Reference
                    </Text>
                    <Text fw={500} size="sm">
                      {sendOut.authorizationReference}
                    </Text>
                  </Box>
                )}
              </Group>

              <Divider />

              {/* Materials Section - Resolved Specimens */}
              {(!sendOut.resolvedMaterials ||
                sendOut.resolvedMaterials.length === 0) && (
                <Box>
                  <Text fw={600} mb={4}>
                    Materials Requested
                  </Text>
                  <Paper withBorder p="sm" bg="gray.0" radius="sm">
                    <Text size="sm" c="dimmed">
                      No specific materials requested — pathologist to determine
                      at approval.
                    </Text>
                  </Paper>
                </Box>
              )}
              {sendOut.resolvedMaterials &&
                sendOut.resolvedMaterials.length > 0 && (
                  <Box>
                    <Group mb="xs">
                      <Text fw={600}>Materials Requested</Text>
                      <Text size="sm" c="dimmed">
                        {slideCount} slide{slideCount !== 1 ? "s" : ""} across{" "}
                        {resolvedBlockCount} block
                        {resolvedBlockCount !== 1 ? "s" : ""}
                      </Text>
                    </Group>
                    <Group align="stretch" gap="sm" wrap="wrap">
                      {sendOut.resolvedMaterials.map((item) => (
                        <Paper
                          key={item.specimen.id}
                          withBorder
                          p="xs"
                          radius="sm"
                          style={{ minWidth: 180 }}
                        >
                          <Group gap="xs" mb={4}>
                            <IconFlask
                              size={14}
                              color="var(--mantine-color-indigo-6)"
                            />
                            <Text fw={600} size="sm">
                              {item.specimen.number}
                            </Text>
                            {item.specimen.source && (
                              <Badge color="indigo" variant="light" size="xs">
                                {item.specimen.source.name}
                              </Badge>
                            )}
                          </Group>
                          {item.blocks.map((block) => {
                            const isBlockSent =
                              sendOut.blocksSent?.includes(block.id) ?? false;

                            return (
                              <Box key={block.id} ml="sm" mb={2}>
                                <Group gap={4} mb={2}>
                                  <IconCube size={10} color="gray" />
                                  <Text
                                    size="xs"
                                    fw={isBlockSent ? 500 : 400}
                                    c={isBlockSent ? undefined : "dimmed"}
                                    fs={isBlockSent ? undefined : "italic"}
                                  >
                                    Block {block.number}
                                  </Text>
                                  {!isBlockSent && (
                                    <Badge
                                      size="xs"
                                      variant="light"
                                      color="gray"
                                    >
                                      parent only — not sent
                                    </Badge>
                                  )}
                                </Group>
                                {block.slides.length > 0 && (
                                  <Stack gap={1} ml="md">
                                    {block.slides.map((slide) => (
                                      <Group key={slide.id} gap={4}>
                                        <Text size="xs" c="dimmed">
                                          Slide {slide.number}
                                        </Text>
                                        {slide.stain && (
                                          <Badge
                                            size="xs"
                                            variant="outline"
                                            color="dark"
                                          >
                                            {slide.stain.code}
                                          </Badge>
                                        )}
                                      </Group>
                                    ))}
                                  </Stack>
                                )}
                              </Box>
                            );
                          })}
                        </Paper>
                      ))}
                    </Group>
                  </Box>
                )}

              {/* Reason */}
              {(sendOut.externalInstitutionReason || sendOut.reason) && (
                <>
                  <Divider />
                  <Box>
                    <Text size="xs" c="dimmed" mb={4}>
                      Reason
                    </Text>
                    <Paper withBorder p="sm" bg="gray.0" radius="sm">
                      {sendOut.externalInstitutionReason ? (
                        <Text size="sm">
                          {formatExternalInstitutionReason(
                            sendOut.externalInstitutionReason,
                            sendOut.externalInstitutionReasonOther
                          )}
                        </Text>
                      ) : (
                        <div
                          style={{ fontSize: "var(--mantine-font-size-sm)" }}
                          // eslint-disable-next-line react/no-danger
                          dangerouslySetInnerHTML={{
                            __html: sendOut.reason || "",
                          }}
                        />
                      )}
                    </Paper>
                  </Box>
                </>
              )}

              {/* Special Instructions */}
              {sendOut.specialInstructions && (
                <Box>
                  <Text size="xs" c="dimmed" mb={4}>
                    Special Instructions
                  </Text>
                  <Paper withBorder p="sm" bg="gray.0" radius="sm">
                    <div
                      style={{ fontSize: "var(--mantine-font-size-sm)" }}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html: sendOut.specialInstructions,
                      }}
                    />
                  </Paper>
                </Box>
              )}

              {/* Legacy Notes */}
              {sendOut.notes && (
                <Box>
                  <Text size="xs" c="dimmed" mb={4}>
                    Notes
                  </Text>
                  <Paper withBorder p="sm" bg="gray.0" radius="sm">
                    <div
                      style={{ fontSize: "var(--mantine-font-size-sm)" }}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: sendOut.notes || "" }}
                    />
                  </Paper>
                </Box>
              )}
            </Stack>
          </ScrollArea.Autosize>

          {/* Sticky Footer */}
          <Group
            justify="flex-end"
            pt="md"
            mt="md"
            style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }}
          >
            <Button
              variant="subtle"
              color="red"
              leftSection={<IconX size={16} />}
              onClick={() => setView("reject")}
            >
              Reject
            </Button>
            <Button
              color="green"
              leftSection={<IconCheck size={16} />}
              onClick={() => setView("confirm-approve")}
            >
              Approve & Send to Shipping
            </Button>
          </Group>
        </>
      ) : view === "confirm-approve" ? (
        // ===== CONFIRM APPROVE VIEW =====
        <Stack gap="md" p={4}>
          <Alert
            color="green"
            title="Confirm Approval"
            icon={<IconCheck size={18} />}
          >
            This send-out will be approved and moved to the shipping queue for
            packaging and dispatch.
          </Alert>

          <Group justify="space-between" pt="md">
            <Button
              variant="subtle"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => setView("review")}
            >
              Back
            </Button>
            <Button
              color="green"
              leftSection={<IconCheck size={16} />}
              onClick={handleApprove}
              loading={approveMutation.isPending}
            >
              Confirm Approval
            </Button>
          </Group>
        </Stack>
      ) : (
        // ===== REJECT VIEW =====
        <form
          noValidate
          onSubmit={rejectForm.onSubmit(handleReject, () =>
            scrollToFirstError(rejectForm)
          )}
        >
          <Stack gap="md" p={4}>
            <Alert
              color="red"
              title="Reject Send-Out"
              icon={<IconAlertTriangle size={18} />}
            >
              This send-out request will be rejected and the requesting
              pathologist will be notified.
            </Alert>

            <Textarea
              label="Rejection Reason"
              placeholder="Provide a reason for rejecting this send-out request..."
              minRows={3}
              withAsterisk
              {...rejectForm.getInputProps("rejectionReason")}
            />

            <Group justify="space-between" pt="md">
              <Button
                variant="subtle"
                leftSection={<IconArrowLeft size={16} />}
                onClick={() => setView("review")}
              >
                Back
              </Button>
              <Button
                type="submit"
                color="red"
                loading={rejectMutation.isPending}
              >
                Confirm Rejection
              </Button>
            </Group>
          </Stack>
        </form>
      )}
    </Modal>
  );
}
